import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { HashLink } from './HashLink';

import useHover from '@rio-cloud/rio-uikit/useHover';

const DEFAULT_HEADLINE = 'h2';
const HASH_LINK_ROUTE_SELECTOR = '[data-hash-link-route]';

type HeadlineProps = {
    anchor?: string;
    children: React.ReactNode;
    className?: string;
    hashLink?: boolean;
    headlineType?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
    headlineNoTopSpace?: boolean;
};

const getHeadlineHashLink = (anchorElement: HTMLElement | null, pathname: string, search: string, anchor: string) => {
    const routeElement = anchorElement?.closest(HASH_LINK_ROUTE_SELECTOR) as HTMLElement | null;
    const route = routeElement?.dataset.hashLinkRoute ?? `${pathname}${search}`;
    const rootAnchor = routeElement?.dataset.hashLinkRootAnchor;
    const hash = anchor === rootAnchor ? '' : `#${anchor}`;

    return `${route}${hash}`;
};

export const Headline = (props: HeadlineProps) => {
    const {
        anchor,
        children,
        className = '',
        hashLink = true,
        headlineType = DEFAULT_HEADLINE,
        headlineNoTopSpace,
    } = props;

    const { pathname, search } = useLocation();

    const anchorRef = useRef<HTMLAnchorElement>(null);
    const headlineRef = useRef(null);

    const isHeadlineHover = useHover(headlineRef);

    const [hashLinkTarget, setHashLinkTarget] = useState(anchor ? `${pathname}${search}#${anchor}` : '');

    useEffect(() => {
        if (!anchor) {
            return;
        }

        setHashLinkTarget(getHeadlineHashLink(anchorRef.current, pathname, search, anchor));
    }, [anchor, pathname, search]);

    const headlineText = React.createElement(
        headlineType,
        {
            className: `display-flex align-items-center${headlineNoTopSpace ? ' margin-top-0' : ''}`,
            ref: headlineRef,
        },
        <>
            <span>{children}</span>
            {anchor && hashLink && isHeadlineHover && (
                <HashLink to={hashLinkTarget} className='text-decoration-none margin-left-5'>
                    <span className='uikit-icon icon-link' />
                </HashLink>
            )}
        </>
    );

    return (
        <div className={className}>
            {anchor &&
                (hashLink ? (
                    <a className='anchor' id={anchor} ref={anchorRef} />
                ) : (
                    <a className='anchor' id={anchor} data-sync-hash='false' ref={anchorRef} />
                ))}
            <div className='description-headline'>{headlineText}</div>
        </div>
    );
};
